import { select, selectAll, on, addClass, removeClass, createNodes, append, getData } from 'modules/dom'
import { getProp, pipe, then, map, addQueryVar, getQueryVar, fetchData, parseOptions } from 'modules/utils'
import { getTimes, hasNextCalendarButton, hasPrevCalendarButton, hasNextAndPrevButtons, setDataAttributes, addQueryVars, removeQueryVars } from '../../src/js/lib/webinars'
import { initializeModuleForEl } from '../../src/js/lib/init-modules.js'
import {
  isSameMonth,
  isSameYear,
  getNextDay,
  getPrevDay,
  getNextMonth,
  getPrevMonth,
  nextDayAfterWeek,
  prevDayBeforeWeek,
  getMonthRange,
  getWeekRange,
  loadFormatDate,
  isDate,
} from '../../src/js/lib/date'

const loadingClass = 'webinar-list--loading'

/**
 * Generate a calendar title from given date range
 * @param {Date} sunday
 * @param {Date} saturday
 * @returns {string}
 */
const getCalendarTitle = (sunday, saturday) => {
  if (!isDate(sunday) || !isDate(saturday)) {
    return ''
  }
  let result = ''
  let separator = ' - '
  if ( isSameYear(sunday, saturday) ) {
    if ( isSameMonth(sunday, saturday) ) {
      // Example: January 02 - 04, 2019
      result = loadFormatDate(sunday, 'F dd')
      result += separator
      result += loadFormatDate(saturday, 'dd, YYYY')
    } else {
      // Example: January 31 - February 02, 2019
      result = loadFormatDate(sunday, 'F dd')
      result += separator
      result += loadFormatDate(saturday, 'F dd, YYYY')
    }
  } else {
    // Example: December 31, 2019 - January 04, 2020
    result = loadFormatDate(sunday, 'F dd, YYYY')
    result += separator
    result += loadFormatDate(saturday, 'F dd, YYYY')
  }

  return result
}

const queryVars = ['currentStart', 'activeStart', 'activeEnd']

export default (el) => {
  const boxNull = select('.js-webinar-list-null')
  const nextButton = select('.js-calendar-next', el)
  const prevButton = select('.js-calendar-prev', el)
  const selectElm = select('.js-calendar-select', el)
  const calendarTitleElm = select('.js-calendar-title', el)
  const list = select('.js-grid-block-list')

  /**
   * Add query arguments to page URL
   * @param {string} pageUrl
   * @param {Object} timeObject
   */
  const addQueryArgs = (pageUrl, timeObject) => {
    addQueryVars(pageUrl, timeObject)
  }

  const updateFilterHeader = (e) => {
    const viewSelectEl = e.target
    const viewBySelected = selectElm.value
    // Get Query Var from URL
    let activeStartVar = getQueryVar('activeStart')
    let activeEndVar = getQueryVar('activeEnd')
    let currentTimeVar = getQueryVar('currentStart')

    // Create variables
    let currentDate = new Date()
    let newDate
    let pageUrl = addQueryVar('view-by', viewBySelected)

    // These variables will be output
    let dates = {}
    let title = ''
    let current, start, end

    current = currentTimeVar ? new Date(currentTimeVar) : new Date()

    if (activeStartVar && activeEndVar) {
      start = new Date(activeStartVar)
      end = new Date(activeEndVar)
      dates = getTimes(current, start, end, [], 'iso8601')

      removeQueryVars(pageUrl, queryVars)
    }

    switch(viewBySelected) {
      case 'timeGridDay':
        if (hasNextCalendarButton(viewSelectEl)) {
          current = getNextDay(current)
        }

        if (hasPrevCalendarButton(viewSelectEl)) {
          current = getPrevDay(current)
        }

        start = end = current

        dates = getTimes(current, start, end, queryVars, 'iso8601')
        title = loadFormatDate(current, 'F dd, YYYY')

        break;

      case 'dayGridMonth':
        newDate = getMonthRange(currentDate)
        let nextMonth, prevMonth

        if (hasNextCalendarButton(viewSelectEl)) {
          nextMonth = getNextMonth(current)
          newDate = getMonthRange(nextMonth)
        }

        if (hasPrevCalendarButton(viewSelectEl)) {
          prevMonth = getPrevMonth(current)
          newDate = getMonthRange(prevMonth)
        }

        current = newDate.startDay
        start = current
        end = newDate.endDay


        dates = getTimes(current, start, end, queryVars, 'iso8601')
        title = loadFormatDate(current, 'F YYYY')

        break;

      case 'timeGridWeek':
        newDate = getWeekRange(currentDate)

        if (hasNextCalendarButton(viewSelectEl)) {
          let nextWeek = nextDayAfterWeek(current)
          newDate = getWeekRange(nextWeek)
        }

        if (hasPrevCalendarButton(viewSelectEl)) {
          let prevWeek = prevDayBeforeWeek(current)
          newDate = getWeekRange(prevWeek)
        }

        current = newDate.startDay
        start = current
        end = newDate.endDay

        dates = getTimes(current, start, end, queryVars, 'iso8601')
        title = getCalendarTitle(start, end)

        break;
    }

    addQueryArgs(pageUrl, dates)
    setDataAttributes(calendarTitleElm, dates)
    calendarTitleElm.innerHTML = title

    const elementObj = {
      elm: viewSelectEl
    }

    // Merge two objects
    return {...elementObj, ...dates}
  }


  const removeLoadingState = () => {
    removeClass(loadingClass, el)
  }

  const getEndPoint = (object) => {
    const { elm, currentStart, activeStart, activeEnd } = object
    const viewBy = selectElm.value

    let url = `${window.location.origin}/wp-json/ecl/v1/webinar-filter?view-by=${viewBy}`
    if ((activeStart && activeEnd) || hasNextAndPrevButtons(elm)) {
      url += `&currentStart=${currentStart}&activeStart=${activeStart}&activeEnd=${activeEnd}`
    }
    return url
  }

  const updateList = (response) => {
    if (typeof response === 'string') {
      response = parseOptions(response)
    }
    if (getProp('html', response) === '') {
      list.innerHTML = ''
      addClass('active', boxNull)
    } else {
      removeClass('active', boxNull)
      const nodes = pipe(
        getProp('html'),
        createNodes
      )(response)

      list.innerHTML = ''
      append(list, nodes)

      // Initialize all modules inside the nodes
      map(node => {
        const modules = selectAll('[data-module-init]', node)
        map(initializeModuleForEl, modules)
      }, nodes)

      // Initialize the modules of nodes
      map(initializeModuleForEl, nodes)

      // Initialize the login modal
      const modalLoginEl = select('[data-module-init="modal-login"]')
      modalLoginEl && initializeModuleForEl(modalLoginEl)
    }

    setTimeout(removeLoadingState, 200)
  }

  const updateFilter = pipe(
    updateFilterHeader,
    getEndPoint,
    fetchData,
    then(updateList)
  )

  const handleClickEvent = on('click', updateFilter)

  if (nextButton) {
    handleClickEvent(nextButton)
  }
  if (prevButton) {
    handleClickEvent(prevButton)
  }
  if (selectElm) {
    on('change', updateFilter, selectElm)
  }
}
